import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Users, Car, IdCard, Building2, FileSearch, Upload, Activity, Clock, ShieldCheck } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ACTION_LABELS = {
  LOGIN: "Connexion", LOGIN_FAILED: "Echec de connexion", LOGOUT: "Deconnexion",
  SEARCH: "Recherche", VIEW_RECORD: "Consultation", CREATE_RECORD: "Creation",
  UPDATE_RECORD: "Modification", IMPORT: "Importation", EXPORT: "Export",
  CREATE_USER: "Creation utilisateur", UPDATE_USER: "Modification utilisateur",
  CHANGE_ROLE: "Changement de role", DISABLE_USER: "Suspension", RESET_PASSWORD: "Reinit. mot de passe",
  CHANGE_SETTINGS: "Parametres",
};

export function fmtDate(ts) {
  if (!ts) return "-";
  const d = new Date(ts);
  return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    API.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({ counts: {} }));
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) navigate(`/recherche?q=${encodeURIComponent(q.trim())}`);
  };

  const c = data?.counts || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm">
        <div className="tricolor" />
        <div className="p-6 sm:p-7">
          <div className="min-w-0">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#009E60]">Republique Gabonaise</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold tracking-tight" data-testid="dashboard-greeting">
              Bonjour, {user?.first_name}
            </h1>
            <p className="mt-1 text-sm font-semibold text-foreground/80" data-testid="dashboard-ministry">
              Ministere des Transports, de la Marine Marchande, charge de la Logistique
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full">
        <div className="rounded-2xl bg-gradient-to-r from-[#009E60] via-[#FCD116] to-[#3A75C4] p-[3px] shadow-xl">
        <form onSubmit={submit}
          className="rounded-[13px] bg-card p-2"
          data-testid="hero-search">
          <div className="flex items-center gap-2">
            <Search className="ml-3 h-5 w-5 text-muted-foreground shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, prenom, telephone, permis, immatriculation, VIN, structure..."
              className="w-full bg-transparent px-2 py-3 text-base sm:text-lg focus:outline-none"
              data-testid="hero-search-input"
            />
            <Button type="submit" className="bg-[#009E60] hover:bg-[#00804e] text-white font-semibold px-5 h-11 rounded-xl shrink-0" data-testid="hero-search-submit">
              Rechercher
            </Button>
          </div>
        </form>
        </div>
        <div className="mt-2 flex flex-wrap justify-center gap-2 text-[11px] text-muted-foreground">
          <span className="rounded-full bg-accent px-2.5 py-1">Ex : MBOUMBA</span>
          <span className="rounded-full bg-accent px-2.5 py-1">GA-123-AA</span>
          <span className="rounded-full bg-accent px-2.5 py-1">TEST-00042</span>
          <span className="rounded-full bg-accent px-2.5 py-1">Toyota</span>
          <span className="rounded-full bg-accent px-2.5 py-1">+241 06...</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="dashboard-stats">
        {!data ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
        ) : (
          <>
            <StatCard label="Conducteurs" value={c.conducteurs ?? 0} icon={Users} color="green" testid="stat-conducteurs" />
            <StatCard label="Vehicules" value={c.vehicules ?? 0} icon={Car} color="blue" testid="stat-vehicules" />
            <StatCard label="Permis" value={c.permis ?? 0} icon={IdCard} color="yellow" testid="stat-permis" />
            <StatCard label="Structures" value={c.structures ?? 0} icon={Building2} color="slate" testid="stat-structures" />
            <StatCard label="Recherches aujourd'hui" value={c.recherches_aujourdhui ?? 0} icon={FileSearch} color="blue" testid="stat-recherches" />
            <StatCard label="Importations" value={c.importations ?? 0} icon={Upload} color="green" testid="stat-imports" />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="dashboard-evolution">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Activity className="h-4 w-4 text-[#3A75C4]" /> Evolution des enregistrements
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!data ? <Skeleton className="h-56 w-full" /> : (
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={data.evolution || []}>
                  <defs>
                    <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#009E60" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#009E60" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3A75C4" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3A75C4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} />
                  <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={40} />
                  <RTooltip />
                  <Area type="monotone" dataKey="conducteurs" stroke="#009E60" fill="url(#gc)" strokeWidth={2} />
                  <Area type="monotone" dataKey="vehicules" stroke="#3A75C4" fill="url(#gv)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card data-testid="dashboard-status">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#009E60]" /> Etat du systeme
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Plateforme</span>
              <StatusBadge value="ACTIF" testid="system-status" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Version</span>
              <span className="mono text-xs">{data?.system?.version || "1.0.0"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Base de donnees</span>
              <StatusBadge value="ACTIF" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Journalisation</span>
              <StatusBadge value="ACTIF" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card data-testid="dashboard-activity">
          <CardHeader><CardTitle className="text-base font-heading">Activite recente</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!data ? <Skeleton className="h-40 w-full" /> : (data.recent_activity || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune activite.</p>
            ) : (data.recent_activity || []).slice(0, 7).map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-2 text-sm border-b border-border/50 pb-2 last:border-0">
                <div>
                  <p className="font-medium">{ACTION_LABELS[a.action] || a.action}</p>
                  <p className="text-xs text-muted-foreground">{a.username || "systeme"}</p>
                </div>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(a.ts)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="dashboard-imports">
          <CardHeader><CardTitle className="text-base font-heading">Dernieres importations</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!data ? <Skeleton className="h-40 w-full" /> : (data.recent_imports || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune importation pour le moment.</p>
            ) : (data.recent_imports || []).map((im) => (
              <div key={im.id} className="flex items-center justify-between gap-2 text-sm border-b border-border/50 pb-2 last:border-0">
                <div className="min-w-0">
                  <p className="font-medium truncate">{im.filename}</p>
                  <p className="text-xs text-muted-foreground">{im.total_rows} ligne(s)</p>
                </div>
                <StatusBadge value={im.status} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="dashboard-recent-searches">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Clock className="h-4 w-4 text-[#3A75C4]" /> Mes recherches recentes</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {!data ? <Skeleton className="h-40 w-full" /> : (data.recent_searches || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune recherche recente.</p>
            ) : (data.recent_searches || []).map((s, i) => (
              <Link key={i} to={`/recherche?q=${encodeURIComponent(s.query)}`}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent transition-colors"
                data-testid={`recent-search-${i}`}>
                <span className="font-medium truncate">{s.query}</span>
                <span className="text-xs text-muted-foreground">{s.total} resultat(s)</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
